import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { EventFormPage } from './forms/EventFormPage';
import ConfirmDialog from './ui/ConfirmDialog';
import { MetricCard } from './MetricCards';
import LoadErrorBox from './ui/LoadErrorBox';
import { EVENT_PERMANENT_DELETE, EVENT_RESTORE } from './header/EventsBar';
import { setEventsBar } from '../hooks/useEventsBar';
import { useDeleteConfirm } from '../hooks/useDeleteConfirm';
import { useFormDeleteEvent } from './forms/_shared';
import { useMoney } from '../hooks/useMoney';
import { useRole } from '../hooks/useRole';
import {
  archiveEvent, deleteEventPermanently, restoreEvent, type StoredEvent,
} from '../services/EventsService';
import { loadTasks, refreshTasksFromApi } from '../services/TasksService';
import { errorMessageOf } from '../utils/apiErrors';
import { useNotifications } from './NotificationProvider';
import '../styles/components/EventsScreens.css';

type Tab = 'details' | 'guests' | 'tasks' | 'money';

interface EventDetailProps {
  eventId: string;
  onBack: () => void;
}

const EventDetail: React.FC<EventDetailProps> = ({ eventId, onBack }) => {
  const { showSuccess, showError } = useNotifications();
  const { canViewFinancials, canEdit } = useRole();
  const money = useMoney();
  const [tab, setTab] = useState<Tab>('details');
  const [event, setEvent] = useState<StoredEvent | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  // Bumped by Retry: remounts the form, which loads the event again.
  const [attempt, setAttempt] = useState(0);
  const [tasks, setTasks] = useState(() => loadTasks());

  const publish = useCallback((e: StoredEvent) => {
    setEvent(e);
    setLoadError(null);
    setEventsBar({ detail: { id: e.id, title: e.title, archived: !!e.archived } });
  }, []);

  // The form loads the event (and tells us through onLoaded); leaving the page clears the breadcrumb title.
  useEffect(() => () => setEventsBar({ detail: null }), [eventId]);

  useEffect(() => {
    void refreshTasksFromApi().then(setTasks).catch(() => undefined);
  }, []);

  const linkedTasks = useMemo(
    () => tasks.filter(t => !t.archived && t.eventIds?.includes(eventId)),
    [tasks, eventId],
  );

  const del = useDeleteConfirm<StoredEvent>({
    record: 'event',
    perform: e => deleteEventPermanently(e.id),
    onDone: e => { showSuccess(`${e.title} deleted`); onBack(); },
  });

  const handleArchive = useCallback(async () => {
    if (!event) return;
    try {
      await archiveEvent(event.id);
      showSuccess(`${event.title} archived`);
      onBack();
    } catch (err) {
      showError(errorMessageOf(err, `Could not archive ${event.title}.`));
    }
  }, [event, onBack, showError, showSuccess]);

  useFormDeleteEvent(() => { void handleArchive(); });
  const requestDelete = del.request;

  useEffect(() => {
    const onRestore = async () => {
      if (!event) return;
      try {
        publish(await restoreEvent(event.id));
        showSuccess(`${event.title} restored`);
      } catch (err) {
        showError(errorMessageOf(err, `Could not restore ${event.title}.`));
      }
    };
    const onDelete = () => { if (event) requestDelete(event); };
    window.addEventListener(EVENT_RESTORE, onRestore);
    window.addEventListener(EVENT_PERMANENT_DELETE, onDelete);
    return () => {
      window.removeEventListener(EVENT_RESTORE, onRestore);
      window.removeEventListener(EVENT_PERMANENT_DELETE, onDelete);
    };
  }, [event, requestDelete, publish, showError, showSuccess]);

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'details', label: 'Details' },
    { id: 'guests', label: 'Guests' },
    { id: 'tasks', label: `Tasks (${linkedTasks.length})` },
    ...(canViewFinancials ? [{ id: 'money' as Tab, label: 'Budget' }] : []),
  ];

  return (
    <div className="wt-container fade-in">
      <div className="ev-detail-tabs" role="tablist">
        {tabs.map(t => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`ev-detail-tab${tab === t.id ? ' active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loadError ? (
        <LoadErrorBox what="this event" error={loadError} onRetry={() => { setLoadError(null); setAttempt(n => n + 1); }} />
      ) : (
        /* Kept mounted so a half-typed edit survives a visit to another tab. */
        <div role="tabpanel" hidden={tab !== 'details'}>
          <EventFormPage
            key={attempt}
            eventId={eventId}
            archived={event?.archived}
            onLoaded={publish}
            onLoadError={setLoadError}
            onSave={saved => { publish(saved); showSuccess('Event saved'); }}
            onCancel={onBack}
          />
        </div>
      )}

      {tab === 'guests' && event && !loadError && (
        <section className="stats-grid" aria-label="Guests for this event">
          <MetricCard label="Planned headcount" value={event.headcount ?? '—'} icon="users" tone="teal" />
          <MetricCard label="Invited" value={event.rsvpCounts?.invited ?? 0} icon="mail" tone="blue" />
          <MetricCard label="Confirmed" value={event.rsvpCounts?.confirmed ?? event.headcountConfirmed ?? 0} icon="check" tone="green" />
          <MetricCard label="Pending" value={event.rsvpCounts?.pending ?? 0} icon="clock" tone="orange" />
          <MetricCard label="Declined" value={event.rsvpCounts?.declined ?? 0} icon="x" tone="red" />
        </section>
      )}

      {tab === 'tasks' && (
        linkedTasks.length === 0 ? (
          <p className="ev-note">No tasks are linked to this event yet.</p>
        ) : (
          <ul className="ev-detail-list">
            {linkedTasks.map(t => (
              <li key={t.id} className="ev-detail-row">
                <span className="ev-detail-row-title">{t.task}</span>
                <span className="ev-detail-row-meta">{t.status}</span>
              </li>
            ))}
          </ul>
        )
      )}

      {tab === 'money' && canViewFinancials && event && (
        <ul className="ev-detail-list">
          <li className="ev-detail-row">
            <span className="ev-detail-row-title">Price</span>
            <span className="ev-detail-row-meta">{event.price !== '' ? money.format(Number(event.price)) : '—'}</span>
          </li>
          <li className="ev-detail-row">
            <span className="ev-detail-row-title">Budget allocation</span>
            <span className="ev-detail-row-meta">{event.budgetAllocation ? money.format(Number(event.budgetAllocation)) : '—'}</span>
          </li>
        </ul>
      )}

      {!canEdit && <p className="ev-note">You can view this event but not change it.</p>}

      <ConfirmDialog
        {...del.dialogProps}
        title="Delete event forever"
        subject={del.target?.title}
        consequences={['The event is removed for good and cannot be restored.']}
        confirmLabel="Delete forever"
      />
    </div>
  );
};

export default EventDetail;
